import React from 'react';

/**
 * Live in-website preview over the sanctioned **ds-inspector (`dsi-*`) bridge** that ships in the deployed
 * brand websites. The Studio embeds the brand's REAL deployed site in an iframe, handshakes with the
 * bridge, and pins the owner's DRAFT banner source over the live one (`dsi-set-source`) — a pin the site's
 * normal data flow cannot undo. Clicking a banner comes back as `dsi-click`.
 *
 * This replaces the old localhost/OWS-Front `ows-creative` bridge entirely: no bridge-enabled dev build, no
 * guest-token scrape, no tenant identity params, no logo injection (the deployed site renders the brand's
 * real logos), and no sports-carousel injection (the deployed pages carry native banner sources).
 *
 * Cross-origin: the iframe is the brand's own origin. Every inbound message is trust-checked (origin + this
 * iframe's own window + the `dsi-` shape); every outbound message is posted to the brand origin, never `*`.
 */

/** ds-inspector protocol. Envelope is `{ type, payload }`; every type is prefixed `dsi-`. */
export const DSI = {
  INIT: 'dsi-init',
  READY: 'dsi-ready',
  DISPOSE: 'dsi-dispose',
  LIST_SOURCES: 'dsi-list-sources',
  SOURCES: 'dsi-sources',
  GET_SOURCE: 'dsi-get-source',
  SOURCE: 'dsi-source',
  SET_SOURCE: 'dsi-set-source',
  UNSET_SOURCE: 'dsi-unset-source',
  SOURCE_SET: 'dsi-source-set',
  CLICK: 'dsi-click',
  HOVER: 'dsi-hover',
  HOVER_OUT: 'dsi-hover-out',
  RECTS: 'dsi-rects',
  HIGHLIGHT: 'dsi-highlight',
  SET_CAPTURE: 'dsi-set-capture',
} as const;

/** Handshake: re-send `dsi-init` on this cadence until `dsi-ready` (the bridge is inert until it arrives). */
export const HANDSHAKE_INTERVAL_MS = 500;
export const HANDSHAKE_MAX_TRIES = 30;

/** Fallback reveal delay (ms) past the iframe `load` for a site that never announces itself. */
export const SITE_SETTLE_MS = 2200;
/** Hard reveal ceiling (ms): a site that can't be framed may never fire `load`, so reveal anyway rather
 *  than spin forever. */
export const SITE_HARD_REVEAL_MS = 8000;

/** A banner data-source override: the dsId to pin + the site-shaped payload (from Spark's preview-source).
 *  `source` is `{ element_type, meta?, data }` and is pinned verbatim via `dsi-set-source`. */
export interface PreviewDataSource {
  dsId: string;
  source: unknown;
}

/** What the preview is focused on: which carousel's page to show, and (optionally) which banner it opened
 *  from. `null` means "just show the site" (no pin). */
export interface PreviewTarget {
  carousel: string;
  bannerId?: string;
}

export type DsiRect = { x: number; y: number; width: number; height: number };

/** Desktop preview device sizes. The iframe width is constrained (and centred) so the responsive site
 *  renders its tablet / phone layout; `null` fills the stage (true desktop). */
export type DeviceMode = 'desktop' | 'tablet' | 'mobile';
export const DEVICE_WIDTHS: Record<DeviceMode, number | null> = { desktop: null, tablet: 834, mobile: 420 };

/**
 * One data-source container a target sits in, with its own rect, tracked by `dsi-rects` under `nodeId`.
 * The bridge reports the whole chain (innermost first), which is how a hovered game tile names the LIST
 * that rendered it. A site deployed before that bridge change sends no chain, so every reader must cope
 * with it being absent (see `.claude/specs/2026-09-17-preview-game-list-overlays.md`).
 */
export type DsiContainerRef = {
  dsId: string;
  typeId: string | null;
  instId?: string | null;
  nodeId: string;
  rect: DsiRect;
};

/** A hover/click target the bridge reports. */
export type DsiTarget = {
  container: { dsId: string; typeId: string | null };
  item: { index: number | null; id: string | null; name: string | null; data: unknown } | null;
  field: { nodeId: string; links: Record<string, string[]> | null } | null;
  nodeId: string;
  rect: DsiRect;
  /** Every container the target sits in, innermost first; absent on a site with the older bridge. */
  containers?: DsiContainerRef[] | null;
};

/** One entry of `dsi-sources`: a data source currently in the framed site's store. */
export type DsiSourceInfo = { id: string; typeId: string | null; count: number | null; pinned: boolean };

/** A banner click surfaced to the page — mapped from a `dsi-click` Target. Kept as a small shape so the
 *  page's editor-open handler stays independent of the wire protocol. */
export interface BannerClickMessage {
  bannerId?: string;
  index?: number;
  dsId?: string;
  imageUrl?: string;
}

/** The page path each carousel maps to on the deployed brand site. */
const CAROUSEL_PATH: Record<string, string> = { homepage: '/', casino: '/casino', sports: '/sport/home' };
export const pagePathForCarousel = (carousel: string): string =>
  // A path is its own page: the Games tab names pages by the path the site serves them at.
  carousel.startsWith('/') ? carousel : (CAROUSEL_PATH[carousel] ?? '/');

/** The carousel a deployed page path belongs to (inverse of {@link CAROUSEL_PATH}), or null when the
 *  path is some other page (a game, promo, etc.). Used to re-pin the right draft after an in-site
 *  navigation, once the bridge re-announces the new URL. */
export const carouselForPath = (pathname: string): string | null => {
  const path = (pathname || '/').replace(/\/+$/, '') || '/';
  for (const [carousel, p] of Object.entries(CAROUSEL_PATH)) {
    if ((p.replace(/\/+$/, '') || '/') === path) return carousel;
  }
  return null;
};

/** The pathname of a URL string, or '' when it is not parseable. */
export const pathnameOf = (url: string): string => {
  try {
    return new URL(url).pathname;
  } catch {
    return '';
  }
};

/**
 * Build the deployed brand site URL for a carousel's page. Just the brand domain + the carousel's path —
 * no edit-mode flags or identity params (the dsi handshake + origin allowlist handle authorization). A bare
 * domain host is upgraded to https. Returns '' when the domain isn't a usable http(s) URL.
 */
export const resolveSiteUrl = (domain: string | undefined, carousel?: string, lang?: string): string => {
  const raw = (domain ?? '').trim();
  if (!raw) return '';
  const withScheme = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  let url: URL;
  try {
    url = new URL(withScheme);
  } catch {
    return '';
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return '';
  url.pathname = pagePathForCarousel(carousel ?? 'homepage');
  if (lang) url.searchParams.set('lang', lang);
  return url.toString();
};

/** The origin an inbound message must arrive from to be believed, or `null` when `src` is not a resolvable
 *  http(s) URL. */
export const previewOrigin = (src: string | undefined): string | null => {
  if (!src) return null;
  try {
    const url = new URL(src, window.location.href);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.origin : null;
  } catch {
    return null;
  }
};

/**
 * Query params applied to every preview page load. `appEnv=wrapper-mobile` + `disableCookiesAgreement`
 * is the deployed lever that suppresses the site's cookie-consent banner (read in the site's `dev.js`):
 * the branch sets `window.config.disableCookies='1'`, and the cookies module renders nothing. It is the
 * ONLY deployed switch that hides the banner (there is no cookie-only flag), and it is safe here because
 * the site's layout is viewport-driven, so at the preview's desktop width it still renders the desktop
 * site (verified) — `wrapper-mobile` only flips the app-mode flag, not the breakpoint.
 */
export const PREVIEW_PARAMS: Record<string, string> = {
  appEnv: 'wrapper-mobile',
  disableCookiesAgreement: '1',
};

/**
 * The iframe URL for a carousel's page: the brand origin + the carousel's path + {@link PREVIEW_PARAMS}.
 * Only the origin of `siteUrl` is used (its path/query are replaced), so the URL is stable regardless of
 * what `siteUrl` carries. Returns '' when `siteUrl` is not a usable http(s) URL.
 */
export const previewFrameSrc = (siteUrl: string, carousel: string | undefined): string => {
  const origin = previewOrigin(siteUrl);
  if (!origin) return '';
  const url = new URL(origin);
  url.pathname = pagePathForCarousel(carousel ?? 'homepage');
  for (const [key, value] of Object.entries(PREVIEW_PARAMS)) url.searchParams.set(key, value);
  return url.toString();
};

/** Post a `dsi-*` message into the framed brand site, targeted at its exact origin. No-ops when the frame
 *  window is gone. */
export const postToFrame = (
  win: Window | null | undefined,
  origin: string,
  type: string,
  payload?: Record<string, unknown>,
): void => {
  if (!win || !origin) return;
  try {
    win.postMessage({ type, payload: payload ?? {} }, origin);
  } catch {
    /* the frame may have navigated away */
  }
};

/** A `dsi-*` envelope from the framed site. */
type DsiEnvelope = { type: string; payload: Record<string, unknown> };

/** Narrow untrusted `MessageEvent.data` to a `dsi-*` envelope. */
export const asDsiMessage = (data: unknown): DsiEnvelope | null => {
  if (typeof data !== 'object' || data === null) return null;
  const d = data as { type?: unknown; payload?: unknown };
  if (typeof d.type !== 'string' || d.type.indexOf('dsi-') !== 0) return null;
  return { type: d.type, payload: (d.payload as Record<string, unknown>) ?? {} };
};

/** Map a `dsi-click` Target payload to a BannerClickMessage (the banner the owner clicked). */
export const bannerClickFromTarget = (t: DsiTarget): BannerClickMessage => ({
  bannerId: t.item?.id ?? undefined,
  index: t.item?.index ?? undefined,
  dsId: t.container?.dsId,
  imageUrl: undefined,
});

/**
 * Reveal gate for the live-site iframe. `revealed` flips true on whichever comes first: the iframe's `load`
 * plus {@link SITE_SETTLE_MS}, or the hard ceiling {@link SITE_HARD_REVEAL_MS}. Re-hides when `src` changes.
 */
export const useSiteReveal = (
  src: string,
): { revealed: boolean; onLoad: () => void; iframeRef: React.RefObject<HTMLIFrameElement> } => {
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const [revealed, setRevealed] = React.useState(false);
  const timerRef = React.useRef<number | null>(null);

  const clearTimer = React.useCallback((): void => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  React.useEffect(() => {
    setRevealed(false);
    clearTimer();
    if (!src) return clearTimer;
    const hard = window.setTimeout(() => setRevealed(true), SITE_HARD_REVEAL_MS);
    return () => {
      clearTimer();
      window.clearTimeout(hard);
    };
  }, [src, clearTimer]);

  const onLoad = React.useCallback((): void => {
    clearTimer();
    timerRef.current = window.setTimeout(() => setRevealed(true), SITE_SETTLE_MS);
  }, [clearTimer]);

  return { revealed, onLoad, iframeRef };
};
